import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, switchMap, throwError } from 'rxjs';
import { SessionService } from '../services/session.service';
import { TokenRefreshService } from '../services/token-refresh.service';
import { LocalStorageService } from 'src/app/shared/localstorage.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private loggingOut = false;

  constructor(
    private readonly sessionService: SessionService,
    private readonly tokenRefreshService: TokenRefreshService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const isApiRequest = req.url.startsWith(environment.BASE_URL);
    const request = isApiRequest ? this.attachAccessToken(req) : req;

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (!this.shouldAttemptRefresh(error, req, isApiRequest)) {
          if (error?.status === 401 && isApiRequest && !this.isAuthRequest(req.url)) {
            this.logout();
          }
          return throwError(() => error);
        }

        return this.tokenRefreshService.refreshAccessToken().pipe(
          switchMap((accessToken) =>
            next.handle(this.attachAccessToken(req, accessToken))
          ),
          catchError((refreshError) => {
            this.logout();
            return throwError(() => refreshError);
          })
        );
      })
    );
  }

  private attachAccessToken(
    request: HttpRequest<any>,
    accessToken?: string
  ): HttpRequest<any> {
    const currentUser = LocalStorageService.get('currentUser');
    const token = accessToken ?? currentUser?.access_token;

    if (!token) {
      return request;
    }

    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private shouldAttemptRefresh(
    error: HttpErrorResponse,
    request: HttpRequest<any>,
    isApiRequest: boolean
  ): boolean {
    const currentUser = LocalStorageService.get('currentUser');

    return (
      error?.status === 401 &&
      isApiRequest &&
      // Pre-rollout sessions have no refresh token stored, so they cannot be
      // renewed and must fall through to logout.
      !!currentUser?.refresh_token &&
      !this.isAuthRequest(request.url)
    );
  }

  private isAuthRequest(url: string): boolean {
    return (
      url.includes('/auth/token') ||
      url.includes('/auth/refresh/token') ||
      url.includes('/auth/logout') ||
      url.includes('/auth/forgot-password') ||
      url.includes('/auth/reset-password') ||
      // The two challenge endpoints only, not a blanket '/auth/mfa/': here a
      // 401 is a wrong code on an open challenge, but on /enroll, /confirm,
      // /status and /email/send it is an expired session that should refresh.
      url.includes('/auth/mfa/verify') ||
      url.includes('/auth/mfa/challenge/email')
    );
  }

  /**
   * Forced sign-out after an unrecoverable 401.
   *
   * Local-only: the refresh token was already rejected, or never existed. The
   * page the user was on is remembered so login can hand it back.
   */
  private logout(): void {
    if (this.loggingOut) {
      return;
    }

    this.loggingOut = true;
    this.sessionService.expireSession().then(() => {
      this.loggingOut = false;
    });
  }
}
